import { useMemo, useState, useEffect } from 'react';
import { Stop, CalendarDay } from '../types';
import { useAppState } from '../state/store';
import ItineraryRow from './ItineraryRow';
import AccommodationCard from './AccommodationCard';
import { computeNights } from '../utils/nights';
import { getAccommodationForDaySync, getAccommodationForDay, AccommodationInfo } from '../utils/accommodation';
import './DayDetail.css';

interface DayDetailProps {
  day: string;
  calendar: CalendarDay[];
  stops: Stop[];
  onStopClick: (stop: Stop) => void;
  onDayChange?: (day: string) => void;
}

export default function DayDetail({ day, calendar, stops, onStopClick, onDayChange }: DayDetailProps) {
  const { selectedStopId } = useAppState();
  const [accommodationInfo, setAccommodationInfo] = useState<AccommodationInfo | null>(null);

  const dayInfo = useMemo(() => {
    const dayData = calendar.find(d => d.date === day);
    const sorted = [...calendar].sort((a, b) => a.date.localeCompare(b.date));
    const dayIndex = sorted.findIndex(d => d.date === day);
    return {
      dayData,
      dayNumber: dayIndex + 1,
      label: dayData ? `Day ${dayIndex + 1} - ${dayData.date_str} ${day.split('-')[0]}` : '',
      sortedCalendar: sorted,
      dayIndex,
    };
  }, [day, calendar]);

  const handlePreviousDay = () => {
    if (dayInfo.dayIndex > 0 && onDayChange) {
      const previousDay = dayInfo.sortedCalendar[dayInfo.dayIndex - 1];
      onDayChange(previousDay.date);
    }
  };

  const handleNextDay = () => {
    if (dayInfo.dayIndex < dayInfo.sortedCalendar.length - 1 && onDayChange) {
      const nextDay = dayInfo.sortedCalendar[dayInfo.dayIndex + 1];
      onDayChange(nextDay.date);
    }
  };

  const canGoPrevious = dayInfo.dayIndex > 0;
  const canGoNext = dayInfo.dayIndex < dayInfo.sortedCalendar.length - 1;

  const dayStops = useMemo(() => {
    // Filter out accommodation stops from regular stops - they'll be shown separately
    return stops
      .filter(stop => stop.date === day && stop.type !== 'accommodation')
      .sort((a, b) => {
        if (a.slot_order !== b.slot_order) return a.slot_order - b.slot_order;
        if (a.start_time && b.start_time) return a.start_time.localeCompare(b.start_time);
        return 0;
      });
  }, [stops, day]);

  // Group stops by time slot
  const stopsBySlot = useMemo(() => {
    const grouped = new Map<string, Stop[]>();
    dayStops.forEach(stop => {
      const slotName = stop.slot_name || 'Other';
      if (!grouped.has(slotName)) {
        grouped.set(slotName, []);
      }
      grouped.get(slotName)!.push(stop);
    });
    return grouped;
  }, [dayStops]);

  const nightsMap = useMemo(() => computeNights(stops), [stops]);

  // Load accommodation info (sync version for initial render, async for address)
  useEffect(() => {
    // Initial sync load
    const syncInfo = getAccommodationForDaySync(stops, day);
    setAccommodationInfo(syncInfo);

    // Then load address asynchronously if accommodation exists
    if (syncInfo) {
      getAccommodationForDay(stops, day).then(info => {
        if (info) {
          setAccommodationInfo(info);
        }
      });
    } else {
      setAccommodationInfo(null);
    }
  }, [stops, day]);

  return (
    <div className="day-detail">
      <div className="day-detail-header">
        <div className="day-navigation">
          <button
            className="day-nav-arrow"
            onClick={handlePreviousDay}
            disabled={!canGoPrevious}
            aria-label="Previous day"
          >
            ‹‹
          </button>
          <h2 className="day-title">{dayInfo.label}</h2>
          <button
            className="day-nav-arrow"
            onClick={handleNextDay}
            disabled={!canGoNext}
            aria-label="Next day"
          >
            ››
          </button>
        </div>
      </div>
      <div className="day-detail-content">
        {Array.from(stopsBySlot.entries())
          .sort(([slotA], [slotB]) => {
            // Get first stop's slot_order for sorting
            const orderA = stopsBySlot.get(slotA)?.[0]?.slot_order || 0;
            const orderB = stopsBySlot.get(slotB)?.[0]?.slot_order || 0;
            return orderA - orderB;
          })
          .map(([slotName, slotStops]) => (
            <div key={slotName} className="time-slot-group">
              <div className="time-slot-header">
                <h3>{slotName}</h3>
              </div>
              <div className="time-slot-activities">
                {slotStops.map((stop, index) => (
                  <ItineraryRow
                    key={stop.id}
                    stop={stop}
                    index={index}
                    nights={nightsMap.get(stop.id)}
                    isSelected={selectedStopId === stop.id}
                    onClick={() => onStopClick(stop)}
                  />
                ))}
              </div>
            </div>
          ))}
        
        {/* Accommodation Section */}
        {accommodationInfo && (
          <div className="accommodation-section">
            <div className="accommodation-section-header">
              <h3>Accommodation</h3>
            </div>
            <AccommodationCard
              accommodation={accommodationInfo}
              isSelected={selectedStopId === accommodationInfo.stop.id}
              onClick={() => onStopClick(accommodationInfo.stop)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

